import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

// Per-user access restrictions, loaded once for the signed-in user and shared
// across the app so the sidebar, router, and project tabs can gate the UI.
//
// This is UX only — every restriction is independently enforced server-side.
// Admins are never restricted (isPageDenied always returns false for them).

const CHAT_API_BASE = '/api/chat';

const DEFAULT = {
  loading: true,
  deniedPages: new Set(),
  projectScope: 'all',
  isAdmin: false,
  isPageDenied: () => false,
  refresh: () => {},
};

const AccessContext = createContext(DEFAULT);

export const useAccess = () => useContext(AccessContext);

export const AccessProvider = ({ user, children }) => {
  const [state, setState] = useState({
    loading: true,
    deniedPages: new Set(),
    projectScope: 'all',
    isAdmin: !!user?.isAdmin,
  });

  const load = useCallback(async () => {
    if (!user?.id) {
      setState({ loading: false, deniedPages: new Set(), projectScope: 'all', isAdmin: false });
      return;
    }
    // Admins are never restricted — skip the fetch and grant everything.
    if (user.isAdmin) {
      setState({ loading: false, deniedPages: new Set(), projectScope: 'all', isAdmin: true });
      return;
    }
    try {
      const token = await api.ensureAuthenticated();
      const res = await fetch(
        `${CHAT_API_BASE}/user-access.php?action=me&user_id=${encodeURIComponent(user.id)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      if (data.success && data.data) {
        setState({
          loading: false,
          deniedPages: new Set(Array.isArray(data.data.denied_pages) ? data.data.denied_pages : []),
          projectScope: data.data.project_scope === 'assigned' ? 'assigned' : 'all',
          isAdmin: !!data.data.is_admin,
        });
        return;
      }
    } catch (e) {
      /* fall through to permissive default — gating is enforced server-side regardless */
    }
    setState({ loading: false, deniedPages: new Set(), projectScope: 'all', isAdmin: !!user?.isAdmin });
  }, [user?.id, user?.isAdmin]);

  useEffect(() => { load(); }, [load]);

  const value = {
    ...state,
    isPageDenied: (key) => !state.isAdmin && state.deniedPages.has(key),
    refresh: load,
  };

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export default AccessContext;
